import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import { borderPrimary, secondaryText, semiBoldText, smallTextSize } from '../../styling/stylingHelper';
import { MultiSelectFilter, ValueDisplayType } from './MultiSelectFilter';

// Wide enough for one option per row — the popover is far narrower than the
// table-header filters, so a 2-column grid truncated every supplier/category.
const MENU_WIDTH = 260;
const VIEWPORT_MARGIN = 8;

/**
 * Labeled filter button whose popover reuses MultiSelectFilter, rendered as a
 * single full-width column so option labels stay readable.
 * Shared by the inventory Assign and Excel-import modals.
 */
export function LabeledFilterButton({
  label,
  selected,
  onChange,
  options,
  valueType,
}: {
  label: string;
  selected: string[];
  onChange: (values: string[]) => void;
  options: string[] | { id: string; name: string }[];
  valueType: ValueDisplayType;
}) {
  const [open, setOpen] = useState(false);
  const [menuAlign, setMenuAlign] = useState<'left' | 'right'>('left');
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const updateMenuAlign = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth ?? MENU_WIDTH;
    const overflowRight = rect.left + menuWidth > window.innerWidth - VIEWPORT_MARGIN;
    const overflowLeft = rect.right - menuWidth < VIEWPORT_MARGIN;

    if (overflowRight && !overflowLeft) {
      setMenuAlign('right');
    } else {
      setMenuAlign('left');
    }
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuAlign();
    window.addEventListener('resize', updateMenuAlign);
    return () => window.removeEventListener('resize', updateMenuAlign);
  }, [open, options.length, updateMenuAlign]);

  return (
    <div className="relative w-full sm:w-fit" ref={ref}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'w-full sm:w-fit px-3 h-11 rounded-xl border dark:border-[#2D4F68] bg-white dark:bg-zinc-900',
          borderPrimary,
          smallTextSize,
          semiBoldText,
          secondaryText,
          'flex items-center justify-between leading-5 gap-1.5 hover:bg-gray-50 dark:bg-[#14344C]'
        )}
      >
        <span>{label}</span>
        {selected.length > 0 && (
          <span className="bg-darkGreenColor dark:bg-darkGreenColor-dark text-white dark:text-white text-xs rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
            {selected.length}
          </span>
        )}
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-[#000000] dark:text-[#CBD5E1]" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div
          ref={menuRef}
          className={cn(
            // w-[260px] must stay in sync with MENU_WIDTH (Tailwind cannot compile interpolated values).
            'absolute mt-2 z-[100] w-[260px] max-w-[calc(100vw-16px)] rounded-xl border border-[#EAECF0] dark:border-[#2D4F68] bg-white dark:bg-[#0E2B42] shadow-lg p-3',
            menuAlign === 'right' ? 'right-0' : 'left-0'
          )}
        >
          {options.length === 0 ? (
            <p className={cn(smallTextSize, secondaryText)}>—</p>
          ) : (
            <MultiSelectFilter
              label={label}
              selectedValues={selected}
              onValuesChange={onChange}
              availableOptions={options}
              valueType={valueType}
              singleColumn
              maxHeight="max-h-[250px]"
            />
          )}
        </div>
      )}
    </div>
  );
}
